import type { Permission } from "@estelle/domain";

/** The permission catalogue, grouped for one module's checkbox block. */
export interface PermissionGroup {
  module: string;
  permissions: Permission[];
}

/**
 * Group the catalogue by `Permission.module`.
 *
 * `module` and `label` exist on `Permission` precisely so the role builder
 * never has to parse `orders.detail.editTags` to work out which section it
 * belongs to or what to call it — see the comment on `Permission` in
 * `@estelle/domain`. Parsing the key here would silently drift from the
 * catalogue's own `module` field the first time the two disagreed, which is
 * exactly the failure this field was added to rule out.
 *
 * Grouped by first appearance rather than alphabetised: the seeded
 * catalogue is already written module-by-module in spec order (dashboard,
 * orders, catalog, …), and re-sorting it alphabetically would put Catalog
 * ahead of Dashboard for no reason a reviewer would find obvious.
 */
export function groupPermissionsByModule(
  permissions: readonly Permission[],
): PermissionGroup[] {
  const order: string[] = [];
  const byModule = new Map<string, Permission[]>();

  for (const permission of permissions) {
    const existing = byModule.get(permission.module);
    if (existing) {
      existing.push(permission);
    } else {
      byModule.set(permission.module, [permission]);
      order.push(permission.module);
    }
  }

  return order.map((module) => {
    const groupPermissions = byModule.get(module);
    // `module` came from `order`, which is only ever pushed alongside a
    // freshly-created map entry, so the lookup above cannot miss.
    if (!groupPermissions) throw new Error(`unreachable: ${module} missing`);
    return { module, permissions: groupPermissions };
  });
}

/**
 * The handful of module keys whose title-cased form is not what a heading
 * should read — `"gst"` is an initialism, not a word.
 */
const MODULE_LABELS: Record<string, string> = {
  gst: "GST",
};

/** `"orders"` becomes `"Orders"` for a group heading with no declared label. */
export function humaniseModule(module: string): string {
  return (
    MODULE_LABELS[module] ?? module.charAt(0).toUpperCase() + module.slice(1)
  );
}
